import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { forumApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import RichContentEditor from '../../components/RichContentEditor';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Plus, MessagesSquare, ArrowUp, ArrowDown, Pin, Lock } from 'lucide-react';

export default function ForumCategoryPage() {
  const { catId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');
  const [showNew, setShowNew] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, c] = await Promise.all([
        forumApi.listThreads(catId, sort),
        forumApi.listCategories(),
      ]);
      setThreads(t.data);
      setCat(c.data.find(x => x.id === catId));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [catId, sort]);

  const createThread = async () => {
    if (!newTitle.trim()) return;
    setCreating(true);
    try {
      const { data } = await forumApi.createThread({ category_id: catId, title: newTitle, body_html: newBody });
      toast.success('Thread posted');
      setShowNew(false); setNewTitle(''); setNewBody('');
      navigate(`/forum/t/${data.id}`);
    } catch { toast.error('Could not post'); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-4 max-w-5xl" data-testid="forum-category-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/forum')} data-testid="back-to-forum-btn"><ArrowLeft className="w-4 h-4 mr-1" />All categories</Button>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2342] flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <MessagesSquare className="w-6 h-6 text-[#0E7490]" />{cat?.name || 'Category'}
          </h1>
          {cat?.description && <p className="text-sm text-[#64748B] mt-1">{cat.description}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-32" data-testid="forum-sort-trigger"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Recent</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="top">Top voted</SelectItem>
            </SelectContent>
          </Select>
          <Button className="careiq-btn-primary" onClick={() => setShowNew(true)} data-testid="new-thread-btn">
            <Plus className="w-4 h-4 mr-1" />New Thread
          </Button>
        </div>
      </div>

      {loading ? <Loader2 className="w-8 h-8 animate-spin text-[#0E7490]" /> : (
        <div className="space-y-2">
          {threads.map(t => (
            <Card key={t.id} className="careiq-card hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/forum/t/${t.id}`)} data-testid={`forum-thread-${t.id}`}>
              <CardContent className="p-4 flex gap-4">
                <div className="flex flex-col items-center justify-center text-xs min-w-[40px]">
                  <ArrowUp className="w-4 h-4 text-[#94A3B8]" />
                  <div className="font-bold text-[#0A2342]">{t.score}</div>
                  <ArrowDown className="w-4 h-4 text-[#94A3B8]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {t.pinned && <Pin className="w-3.5 h-3.5 text-[#CA8A04]" />}
                    {t.locked && <Lock className="w-3.5 h-3.5 text-[#991B1B]" />}
                    <h3 className="font-semibold text-[#0A2342] truncate">{t.title}</h3>
                  </div>
                  <div className="text-xs text-[#64748B] mt-1 flex items-center gap-2 flex-wrap">
                    <span>by {t.author_name}</span>
                    <span>·</span>
                    <Badge className="bg-[#F1F5F9] text-[#64748B] text-[10px]">{t.author_role}</Badge>
                    <span>·</span>
                    <span>{new Date(t.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="text-right text-xs text-[#64748B] flex flex-col justify-center min-w-[80px]">
                  <div className="font-semibold text-[#0A2342]">{t.post_count}</div>
                  <div>repl{t.post_count === 1 ? 'y' : 'ies'}</div>
                  <div className="mt-1">{t.view_count} views</div>
                </div>
              </CardContent>
            </Card>
          ))}
          {threads.length === 0 && <Card className="careiq-card"><CardContent className="p-10 text-center text-[#64748B]">No threads yet — be the first to post!</CardContent></Card>}
        </div>
      )}

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="max-w-3xl" data-testid="new-thread-dialog">
          <DialogHeader><DialogTitle>Start a new thread</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Thread title" className="text-lg" data-testid="thread-title-input" />
            <RichContentEditor value={newBody} onChange={setNewBody} placeholder="Share details, screenshots, embeds…" minHeight={200} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button className="careiq-btn-primary" onClick={createThread} disabled={creating || !newTitle.trim()} data-testid="thread-post-btn">
              {creating && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />}Post thread
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
